<template>
    <div class="details w-full">
        <div class="d-flex align-items-top justify-content-between px-3 pt-3">
            <div>
                <el-button
                    type="text"
                    v-if="shouldShowBackButton"
                    @click="backToPendingReviews">
                    <i class="el-icon-arrow-left"></i>
                    Back to Pending Reviews
                </el-button>
                <h5 class="mb-0">
                    KYC Review - {{ selectedCompany.name }}
                </h5>
            </div>
            <div class="signup-info-table">
                <table class="table table-sm">
                    <tbody>
                        <tr>
                            <th>IP</th>
                            <td>{{ selectedCompany.pre_signup?.ip_location_detail?.ip || 'N/A' }}</td>
                        </tr>
                        <tr>
                            <th>Location</th>
                            <td>{{ selectedCompany.pre_signup?.ip_location_detail?.location || 'N/A' }}</td>
                        </tr>
                        <tr>
                            <th>Date of Signup</th>
                            <td>{{ selectedCompany.pre_signup?.created_at | fixDateTime }}</td>
                        </tr>
                        <tr>
                            <th>Company Size</th>
                            <td>{{ selectedCompany.pre_signup?.company_size || 'N/A' }}</td>
                        </tr>
                        <tr>
                            <th>Company CRM(s)</th>
                            <td>{{ parseCompanyCrm(selectedCompany.pre_signup?.company_crm) }}</td>
                        </tr>
                        <tr>
                            <th>Teams</th>
                            <td>{{ parseTeams(selectedCompany.pre_signup?.teams) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="d-flex justify-content-end mb-1 mr-2 col-md-4 min-w-60">
                <div class="add-note">
                    <button :loading="isLoadingAddNote"
                            @click="onOpenAddNoteDialogClicked">
                        Add a Note
                    </button>
                </div>
                <div>
                    <label class="status-display-label">
                        Verification Status
                    </label>
                    <div class="d-flex">
                        <div class="status-display"
                            :class="[getStatusClass(selectedCompany.kyc_status)]">
                            {{ parseStatus(selectedCompany.kyc_status) }}
                        </div>
                        <el-button
                            class="status-display__button"
                            type="primary"
                            :loading="isLoadingChangeStatus"
                            :disabled="isLoadingChangeStatus || (selectedCompany.kyc_status === 0 || selectedCompany.kyc_status === 1)"
                            @click="onOpenChangeStatusDialogClicked(true)">
                            Change
                        </el-button>
                    </div>
                </div>
            </div>
        </div>

        <div class="px-3 pt-5">
            <h5 class="pb-3">
                Business Info
            </h5>

            <div class="grid">
                <div>
                    <label class="text-muted mb-0">
                        Legal Name
                    </label>
                    <el-input
                        label="Business Name"
                        readonly
                        disabled
                        :value="selectedCompany.legal_name"
                    />
                </div>

                <div>
                    <label class="text-muted mb-0">
                        Business Type
                    </label>
                    <el-input
                        label="Business Type"
                        readonly
                        disabled
                        :value="selectedCompany.business_type"
                    />

                    <div class="d-flex mt-2">
                        <label class="text-muted mb-0 mt-1 mr-2">
                            Company Status
                        </label>
                        <el-tag mini>
                            {{ companyStatus }}
                        </el-tag>
                    </div>
                </div>

                <div>
                    <label class="text-muted mb-0">
                        Business ID Type
                    </label>
                    <el-input
                        label="Business ID Type"
                        readonly
                        disabled
                        :value="parseBusinessRegistrationIdentifiers(selectedCompany.business_registration_identifier)"
                    />
                </div>

                <div>
                    <label class="text-muted mb-0">
                        Business Registration Number
                    </label>
                    <el-input
                        label="Business Registration Number"
                        readonly
                        disabled
                        :value="selectedCompany.business_registration_number"
                    />
                </div>

                <div>
                    <!-- Regions of Operation -->
                    <label class="text-muted mb-0">
                        Regions of Operation
                    </label>
                    <el-select
                        :disabled="true"
                        v-model="selectedCompany.business_regions_of_operation"
                        class="w-full"
                        placeholder="Regions of Operation"
                        multiple
                        clearable
                        filterable
                    >
                        <el-option
                            v-for="region in RegionsOfOperation"
                            :key="region.value"
                            :label="region.name"
                            :value="region.value"
                        />
                    </el-select>
                </div>

                <div>
                    <label class="text-muted mb-0">
                        Business Industry
                    </label>
                    <el-input
                        label="Business Industry"
                        readonly
                        disabled
                        :value="parseBusinessIndustries(selectedCompany.business_industry)"
                    />
                </div>

                <div>
                    <label class="text-muted mb-0">
                        Website URL
                    </label>
                    <el-input
                        label="Website URL"
                        readonly
                        disabled
                        :value="selectedCompany.website_url"
                    />
                </div>

                <div>
                    <label class="text-muted mb-0">
                        How will you primarily use your phone numbers for calls?
                    </label>
                    <el-input
                        label="How will you primarily use your phone numbers for calls?"
                        readonly
                        disabled
                        :value="selectedCompany.voice_integrity_use_case"
                    />
                </div>

                <div>
                    <label class="text-muted mb-0">
                        Voice Integrity Employee Count
                    </label>
                    <el-input
                        label="Voice Integrity Employee Count"
                        readonly
                        disabled
                        :value="selectedCompany.business_employee_count"
                    />
                </div>

                <div>
                    <label class="text-muted mb-0">
                        Voice Integrity Average Calls Per Day
                    </label>
                    <el-input
                        label="Voice Integrity Average Calls Per Day"
                        readonly
                        disabled
                        :value="selectedCompany.average_business_day_call_volume"
                    />
                </div>
            </div>
        </div>

        <div class="px-3 pt-5">
            <h5 class="pb-3">
                Business Address
            </h5>

            <div class="grid">
                <div>
                    <label class="text-muted mb-0">
                        Street
                    </label>
                    <el-input
                        label="Street"
                        readonly
                        disabled
                        :value="account.street"
                    />
                </div>

                <div>
                    <label class="text-muted mb-0">
                        Legal Country
                    </label>
                    <el-input
                        label="Legal Country"
                        readonly
                        disabled
                        :value="parseCountry(selectedCompany.legal_country)"
                    />
                </div>

                <div>
                    <label class="text-muted mb-0">
                        Country
                    </label>
                    <el-input
                        label="Country"
                        readonly
                        disabled
                        :value="parseCountry(selectedCompany.country)"
                    />
                </div>

                <div>
                    <label class="text-muted mb-0">
                        State/Province/Region
                    </label>
                    <el-input
                        label="State/Province/Region"
                        readonly
                        disabled
                        :value="selectedCompany.region"
                    />
                </div>

                <div>
                    <label class="text-muted mb-0">
                        City
                    </label>
                    <el-input
                        label="City"
                        readonly
                        disabled
                        :value="selectedCompany.city"
                    />
                </div>

                <div>
                    <label class="text-muted mb-0">
                        Postal Code
                    </label>
                    <el-input
                        label="Postal Code"
                        readonly
                        disabled
                        :value="selectedCompany.postal_code"
                    />
                </div>
            </div>
        </div>

        <div class="px-3 pt-5 pb-5">
            <h5 class="pb-3">
                Authorized Representative Information
            </h5>

            <div class="grid">
                <div>
                    <label class="text-muted mb-0">
                        First Name
                    </label>
                    <el-input
                        label="First Name"
                        readonly
                        disabled
                        :value="selectedCompany.auth_rep_first_name"
                    />
                </div>

                <div>
                    <label class="text-muted mb-0">
                        Last Name
                    </label>
                    <el-input
                        label="Last Name"
                        readonly
                        disabled
                        :value="selectedCompany.auth_rep_last_name"
                    />
                </div>

                <div>
                    <label class="text-muted mb-0">
                        Email
                    </label>
                    <el-input
                        label="Email"
                        readonly
                        disabled
                        :value="selectedCompany.auth_rep_email"
                    />
                </div>

                <div>
                    <label class="text-muted mb-0">
                        Phone Number
                    </label>
                    <el-input
                        label="Phone Number"
                        readonly
                        disabled
                        :value="selectedCompany.auth_rep_phone_number"
                    />
                </div>

                <div>
                    <label class="text-muted mb-0">
                        Business Title
                    </label>
                    <el-input
                        label="Business Title"
                        readonly
                        disabled
                        :value="selectedCompany.auth_rep_business_title"
                    />
                </div>

                <div>
                    <label class="text-muted mb-0">
                        Job Position
                    </label>
                    <el-input
                        label="Job Position"
                        readonly
                        disabled
                        :value="selectedCompany.auth_rep_job_position"
                    />
                </div>
            </div>
        </div>

        <div v-if="account.referrer" class="px-3 pt-5 pb-5">
            <h5 class="pb-3">
                Referrer
            </h5>

            <div class="grid">
                <div>
                    <label class="text-muted mb-0">
                        Referrer
                    </label>
                    <el-input
                        label="Referrer"
                        readonly
                        disabled
                        :value="account.referrer"
                    />
                </div>
            </div>
        </div>

        <!-- Braintree Details Section -->
        <div class="px-3 pb-4">
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="text-black mb-0">Payment Details</h5>
                <el-button
                    type="primary"
                    size="small"
                    :loading="loading_braintree_details"
                    :disabled="loading_braintree_details || braintree_details_loaded || !braintree_details_available"
                    @click="loadBraintreeDetails">
                    {{ getBraintreeButtonText }}
                </el-button>
            </div>

            <div v-if="braintree_details">
                <!-- Payment Methods Table -->
                <div class="mb-4">
                    <h6 class="text-black">Payment Methods</h6>
                    <el-table :data="braintree_details.payment_methods" stripe>
                        <el-table-column prop="cardholder_name" label="Cardholder Name"/>
                        <el-table-column prop="token" label="Token"/>
                        <el-table-column prop="type" label="Card Type"/>
                        <el-table-column prop="last_4" label="Last 4"/>
                        <el-table-column prop="expiration_date" label="Expiration"/>
                        <el-table-column label="Default">
                            <template slot-scope="scope">
                                <el-tag size="small" :type="scope.row.is_default ? 'success' : ''">
                                    {{ scope.row.is_default ? 'Yes' : 'No' }}
                                </el-tag>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>

                <!-- Addresses Table -->
                <div>
                    <h6 class="text-black">Addresses</h6>
                    <el-table :data="braintree_details.addresses" stripe>
                        <el-table-column prop="street_address" label="Street"/>
                        <el-table-column prop="extended_address" label="Extended"/>
                        <el-table-column prop="locality" label="City"/>
                        <el-table-column prop="region" label="Region"/>
                        <el-table-column prop="postal_code" label="Postal Code"/>
                        <el-table-column prop="country_code" label="Country"/>
                    </el-table>
                </div>
            </div>
        </div>

        <div class="px-3 pb-4" v-if="account?.risk_assessments?.length > 0">
            <h5 class="text-black">
                Full Risk Evaluation
            </h5>
            <kyc-risk-evaluation-tree :data="account.risk_assessments"/>
        </div>

        <div class="px-3 pb-4" v-if="account_review_logs.length > 0">
            <h5 class="text-black">KYC Logs</h5>
            <kyc-account-log-table
                :data="account_review_logs"
                :parse-status="parseStatus"
                :parse-status-value="parseStatusValue"
                :is-loading="is_loading_account_review_logs"/>
        </div>

        <el-dialog
            title="Select Verification Status"
            width="40%"
            center
            :visible.sync="dialog_change_status_visible">
            <div class="status-selection-container">
                <div
                    :key="status.value"
                    v-for="status in filteredKYCSTATUSES"
                    class="status-option"
                    @click="selected_status = status.value">
                    <div class="status-radio">
                        <el-radio
                            :label="status.value"
                            v-model="selected_status">
                            {{ status.label }}
                        </el-radio>
                    </div>
                    <div class="status-description">
                        <i class="el-icon-info-circle description-icon"></i>
                        <div class="description-text" v-html="formatDescription(status.description)"></div>
                    </div>
                </div>
            </div>

            <div class="note-input-container px-4 pt-4">
                <label class="note-label">
                    <i class="el-icon-edit note-icon"></i>
                    <span>Why are you taking this action?</span>
                </label>
                <div class="textarea-wrapper">
                    <el-input
                        type="textarea"
                        round
                        :rows="5"
                        v-model="note"
                    />
                </div>
            </div>

            <span
                slot="footer"
                class="dialog-footer q-pt-xl">
                <el-button
                    type="primary"
                    round
                    :disabled="!isChangeStatusButtonEnabled"
                    @click="changeStatus">
                    Apply
                </el-button>
            </span>
        </el-dialog>

        <el-dialog
            title="Add Note"
            width="30%"
            center
            :visible.sync="dialog_add_note_visible">
            <div class="d-flex justify-content-center">
                <el-input
                    type="textarea"
                    placeholder="Write here"
                    round
                    :rows="5"
                    v-model="note"
                />
            </div>

            <span
                slot="footer"
                class="dialog-footer q-pt-xl">
                <el-button
                    type="primary"
                    round
                    @click="onSubmitNote">
                    Save Note
                </el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
import businessRegistrationIdentifiers from '../../constants/compliance-business-registration-identifiers'
import businessRegionsOfOperations from '../../constants/compliance-business-regions-of-operation'
import businessIndustries from '../../constants/compliance-business-industries'
import KYC_STATUSES from '../../constants/compliance-kyc-statuses'
import RegionsOfOperation from '../../constants/compliance-business-regions-of-operation'
import kycAccountLogTable from './kyc-account-log-table.vue'
import kycRiskEvaluationTree from './kyc-risk-evaluation-tree.vue'
import KycAccountNotes from './kyc-account-notes.vue'
import * as Company from '../../constants/company'
import { COMPANY_CRM_DISPLAY_NAMES, TEAMS_DISPLAY_NAMES } from '../../../user/constants/signup-options'

export default {
    name: 'KycAccountDetails',

    components: {
        kycAccountLogTable,
        kycRiskEvaluationTree,
        KycAccountNotes
    },

    props: {
        selectedCompany: {
            type: Object,
            required: true
        },

        parseStatus: {
            type: Function,
            required: true
        },

        parseStatusValue: {
            type: Function,
            required: true
        },

        shouldShowBackButton: {
            type: Boolean,
            required: true
        },

        isLoadingChangeStatus: {
            type: Boolean,
            required: false
        },

        isLoadingAddNote: {
            type: Boolean,
            required: false
        },
    },

    data() {
        return {
            KYC_STATUSES,
            RegionsOfOperation,
            is_loading_account_details: false,
            is_loading_account_review_notes: false,
            is_loading_account_review_logs: false,
            account: {},
            account_review_logs: [],
            dialog_change_status_visible: false,
            dialog_submit_customer_profile_visible: false,
            dialog_add_note_visible: false,
            selected_status: null,
            notes: [],
            note: null,
            braintree_details: null,
            loading_braintree_details: false,
            braintree_details_loaded: false,
            braintree_details_available: true
        }
    },

    computed: {
        countries () {
            const countries = window.countriesAndTimezones.getAllCountries()

            // first exclude US & CA, and then extract only id, name fields of the countries
            const countriesArr = Object.values(countries)
                .filter(({id}) => !['US', 'CA'].includes(id))
                .map(({id, name}) => {
                    return {id, name}
                })

            // add US & CA to the top and return
            return [
                {id: "US", name: "United States"},
                {id: "CA", name: "Canada"},
                ...countriesArr,
            ]
        },

        isChangeStatusButtonEnabled () {
            return this.selected_status && this.note
        },

        filteredKYCSTATUSES () {
            return this.KYC_STATUSES.filter(status => status.shouldBeAnOptionToSelect).sort((a, b) => a.order - b.order)
        },

        isLoading () {
            return this.is_loading_account_details || this.is_loading_account_review_notes || this.is_loading_account_review_logs
        },
        companyStatus () {
            return this.selectedCompany.company_status === Company.STATUS_PRIVATE ? 'Private' : 'Public'
        },
        getBraintreeButtonText() {
            if (this.loading_braintree_details) {
                return 'Loading...';
            }
            if (!this.braintree_details_available) {
                return 'Payment Details Not Available';
            }
            if (this.braintree_details_loaded) {
                return 'Details Loaded';
            }
            return 'Load Payment Details';
        }
    },

    watch: {
        selectedCompany: {
            handler () {
                this.account = {
                    ...this.account,
                    company: this.selectedCompany,
                    ...this.selectedCompany
                }

                this.getKycAccountReviewLogs()
            },
            deep: true
        }
    },

    methods: {
        getStatusClass(statusValue) {
            const status = this.KYC_STATUSES.find(s => s.enumValue === statusValue);
            return status ? `status-display-${status.value}` : '';
        },

        parseCountry (countryCode) {
            const country = this.countries.find(country => country.id === countryCode)

            return country ? country?.name : ''
        },

        parseBusinessRegistrationIdentifiers (identifier) {
            const matchedIdentifier = businessRegistrationIdentifiers.find(id => id.value === identifier)

            return matchedIdentifier ? matchedIdentifier?.name : identifier
        },

        parseBusinessRegionsOfOperations (region) {
            const matchedRegion = businessRegionsOfOperations.find(r => r.value === region)

            return matchedRegion ? matchedRegion?.name : region
        },

        parseBusinessIndustries (industry) {
            const matchedIndustry = businessIndustries.find(i => i.value === industry)

            return matchedIndustry ? matchedIndustry?.name : industry
        },

        parseCompanyCrm (companyCrm) {
            if (!companyCrm) {
                return 'N/A'
            }

            return companyCrm.map(crm => COMPANY_CRM_DISPLAY_NAMES[crm] || crm.charAt(0).toUpperCase() + crm.slice(1)).join(', ')
        },

        parseTeams (teams) {
            if (!teams) {
                return 'N/A'
            }

            return teams.map(team => TEAMS_DISPLAY_NAMES[team] || team.charAt(0).toUpperCase() + team.slice(1)).join(', ')
        },

        changeStatus () {
            const status = this.KYC_STATUSES.find(s => s.value === this.selected_status)

            this.$emit('change-status', this.account, status.enumValue, this.note)

            this.dialog_change_status_visible = false
        },

        onSubmitNote () {
            this.$emit('add-note', this.account, this.note)
            this.dialog_add_note_visible = false
        },

        backToPendingReviews () {
            this.$emit('back-to-pending-reviews')
        },

        onOpenChangeStatusDialogClicked (value) {
            if (!this.selected_status) {
                const currentStatus = this.account.company?.kyc_status
                const parsedStatus = this.KYC_STATUSES.find(status => status.enumValue === currentStatus)

                this.selected_status = parsedStatus?.value
            }

            this.dialog_change_status_visible = value
        },

        onOpenAddNoteDialogClicked () {
            this.dialog_add_note_visible = true
        },

        setNoteFieldEmptyAndCloseDialog () {
            this.note = null
            this.dialog_add_note_visible = false
        },

        getKycAccountDetails () {
            this.is_loading_account_details = true

            axios.get(`/api/admin/kyc-signups/${this.selectedCompany.id}`)
                .then(response => {
                    if (response.headers['content-type'] !== 'application/json') {
                        return
                    }

                    this.account = {
                        ...this.selectedCompany,
                        ...response.data
                    }
                })
                .catch(error => {
                    console.log(error)
                    this.$root.handleErrors(error)
                })
                .finally(() => {
                    this.is_loading_account_details = false
                })
        },

        getKycAccountReviewLogs () {
            this.is_loading_account_review_logs = true

            axios.get(`/api/admin/kyc-review-logs/${this.selectedCompany.id}`)
                .then(response => {
                    if (response.headers['content-type'] !== 'application/json') {
                        return
                    }

                    this.account_review_logs = response.data
                })
                .catch(error => {
                    console.log(error)
                })
                .finally(() => {
                    this.is_loading_account_review_logs = false
                })
        },

        formatDescription(description) {
            return description.replace(/<<br>>/g, '<br>');
        },

        loadBraintreeDetails() {
            this.loading_braintree_details = true;
            this.braintree_details = null;
            this.braintree_details_loaded = false;

            // Load all Braintree details in a single request
            axios.get(`/api/admin/company/${this.selectedCompany.id}/braintree-details`)
                .then(response => {
                    if (response.headers['content-type'] !== 'application/json') {
                        return;
                    }

                    this.braintree_details = response.data;
                    this.braintree_details_loaded = true;
                    this.braintree_details_available = true;
                })
                .catch(error => {
                    console.log(error);
                    this.braintree_details_available = false;
                    this.braintree_details_loaded = true;

                    let errorMessage = 'Failed to fetch payment details (via Braintree).';
                    if (error.response?.status === 404) {
                        errorMessage = 'No payment details available for this company (via Braintree).';
                    } else if (error.response?.data?.message) {
                        errorMessage = error.response.data.message;
                    }

                    this.$notify({
                        title: 'Warning',
                        message: errorMessage,
                        type: 'warning',
                        showClose: true,
                        duration: 5000
                    });
                })
                .finally(() => {
                    this.loading_braintree_details = false;
                });
        }
    },

    created () {
        this.getKycAccountDetails()
        this.getKycAccountReviewLogs()
    }
}
</script>

<style scoped>
.status-selection-container {
    padding: 0 20px;
    word-break: break-word;
}

.status-option {
    display: flex;
    align-items: flex-start;
    margin-bottom: 20px;
    padding: 15px;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    transition: all 0.3s;
    cursor: pointer;
}

.status-option:hover {
    background-color: #f5f7fa;
    border-color: #409EFF;
}

.status-radio {
    min-width: 200px;
    padding-right: 20px;
}

.status-description {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    color: #606266;
    font-size: 14px;
    line-height: 1.5;
    font-style: italic;
}

.description-icon {
    color: #909399;
    font-size: 16px;
    margin-top: 2px;
}

.description-text {
    flex: 1;
}

/* Style for when option is selected */
.status-option:has(.el-radio.is-checked) {
    background-color: #ecf5ff;
    border-color: #409EFF;
}

.status-option:has(.el-radio.is-checked) .status-description {
    color: #409EFF;
}

.status-option:has(.el-radio.is-checked) .description-icon {
    color: #409EFF;
}

.note-input-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 8px;
}

.note-label {
    display: flex;
    align-items: center;
    color: #606266;
    font-size: 14px;
    line-height: 1.5;
    margin-bottom: 0;
}

.textarea-wrapper {
    width: 100%;
}

.note-icon {
    margin-right: 8px;
    font-size: 14px;
    color: #909399;
}

/* Remove any conflicting styles */
.textarea-label {
    display: none;
}

.signup-info-table {
  min-width: 300px;
}

.signup-info-table table {
  width: 100%;
  margin-bottom: 0;
}

.signup-info-table th {
  font-size: 0.85rem;
  color: #606266;
  padding: 5px 10px;
}

.signup-info-table td {
  font-size: 0.85rem;
  padding: 5px 10px;
}
</style>
